/**
 * Centralized push-notification client.
 *
 * Two code paths share one surface (`enablePush` / `disablePush` /
 * `onForegroundMessage`):
 *   - Web (browser / PWA) → `firebase/messaging`
 *   - Capacitor (iOS, Android) → `@ah/native/push` (which wraps
 *     `@capacitor/push-notifications`)
 *
 * Callers — the toggle component, the auto-prompt, App.tsx — never branch on
 * platform; they just use the exports here. The branch happens at runtime
 * via `isNative()`.
 */

import { initializeApp, getApps, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  deleteToken,
  type Messaging,
} from 'firebase/messaging';
import { isNative } from '@ah/native/runtime';
import {
  requestNativePushToken,
  clearNativePushRegistration,
  onNativePushReceived,
  onNativePushTapped,
} from '@ah/native/push';

const TOKEN_STORAGE_KEY = 'gt_fcm_token';

export interface PushClientConfig extends FirebaseOptions {
  vapidKey: string;
}

export interface RegisterTokenFn {
  (token: string): Promise<void>;
}
export interface UnregisterTokenFn {
  (token: string): Promise<void>;
}

let config: PushClientConfig | null = null;
let registerFn: RegisterTokenFn | null = null;
let unregisterFn: UnregisterTokenFn | null = null;

/**
 * Called once at app startup (from apps/desktop) to hand us the Firebase
 * project config and the GraphQL-backed register/unregister helpers.
 *
 * The module can't talk to the GraphQL client directly because it lives one
 * layer below the app in the workspace DAG — so the app injects functions.
 */
export function configurePushClient(
  cfg: PushClientConfig,
  register: RegisterTokenFn,
  unregister: UnregisterTokenFn,
): void {
  config = cfg;
  registerFn = register;
  unregisterFn = unregister;
}

function app(): FirebaseApp | null {
  if (!config) return null;
  // Missing required Firebase config (e.g. a fresh dev clone without the
  // relevant .env entries) — log once and disable push rather than crashing
  // every consumer of onForegroundMessage().
  if (!config.apiKey || !config.projectId || !config.appId) {
    if (!warnedMissingConfig) {
      console.warn('[notifications] Firebase config incomplete — push notifications disabled.');
      warnedMissingConfig = true;
    }
    return null;
  }
  const existing = getApps()[0];
  if (existing) return existing;
  const { vapidKey: _unused, ...firebaseConfig } = config;
  return initializeApp(firebaseConfig);
}

let warnedMissingConfig = false;

async function messaging(): Promise<Messaging | null> {
  if (!(await isSupported())) return null;
  const a = app();
  if (!a) return null;
  return getMessaging(a);
}

/** Is this device currently set up to receive pushes for this user? */
export async function isEnabled(): Promise<boolean> {
  if (isNative()) {
    // On Capacitor the token-presence check is the source of truth — the
    // OS permission is asked at register time and the plugin caches the
    // token. We mirror that into localStorage for consistency with web.
    return !!localStorage.getItem(TOKEN_STORAGE_KEY);
  }
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  if (!(await isSupported())) return false;
  return !!localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/** Request permission, fetch a token, register it with the API. */
export async function enablePush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!registerFn) return { ok: false, reason: 'pushClient not configured' };

  // Native path — Capacitor plugin handles permission + token. The VAPID key
  // is irrelevant here; FCM/APNs registration uses the bundle-id-bound key
  // configured in the native project's GoogleService-Info.plist.
  if (isNative()) {
    const r = await requestNativePushToken();
    if (!r.ok) return { ok: false, reason: r.reason };
    try {
      await registerFn(r.token);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false, reason: `server-register-failed: ${msg}` };
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, r.token);
    return { ok: true };
  }

  if (!config) return { ok: false, reason: 'pushClient not configured' };
  if (!config.vapidKey) return { ok: false, reason: 'missing VAPID key' };
  if (!(await isSupported())) return { ok: false, reason: 'unsupported' };

  const m = await messaging();
  if (!m) return { ok: false, reason: 'unsupported' };

  // Register under a narrow scope so this SW never claims the root page —
  // otherwise its activate-time clients.claim() steals control from the
  // workbox SW, fires controllerchange, and vite-plugin-pwa's autoUpdate
  // reloads the app mid-onboarding.
  const sw = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  const token = await getToken(m, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: sw,
  });
  if (!token) return { ok: false, reason: 'no token returned' };

  await registerFn(token);
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  return { ok: true };
}

/** Unregister the current token with the API and clear local state. */
export async function disablePush(): Promise<void> {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return;
  try {
    if (unregisterFn) await unregisterFn(token);
  } catch {
    // API call may legitimately fail (user signed out) — proceed with local cleanup.
  }
  localStorage.removeItem(TOKEN_STORAGE_KEY);

  if (isNative()) {
    await clearNativePushRegistration();
    return;
  }

  try {
    const m = await messaging();
    if (m) await deleteToken(m);
  } catch {
    // deleteToken can fail if the token was already revoked upstream — ignore.
  }
}

/**
 * Subscribe to foreground push messages. Returns an unsubscribe function.
 *
 * Fires only for pushes that arrive while the app is foregrounded — does
 * NOT fire on a notification tap (use onNotificationTapped for that).
 *
 * On web this is `isSupported()`-gated (async, so we resolve before wiring).
 * On native it's wired through `@ah/native/push` — Capacitor delivers
 * `notificationReceived` events to JS while the app is foregrounded (iOS
 * won't auto-display a banner; we surface our own toast in this case).
 */
export function onForegroundMessage(
  cb: (payload: { title: string; body: string; data: Record<string, string> }) => void,
): () => void {
  if (isNative()) return onNativePushReceived(cb);

  let stop: (() => void) | null = null;
  let cancelled = false;

  (async () => {
    const m = await messaging();
    if (!m || cancelled) return;
    stop = onMessage(m, (raw) => {
      cb({
        title: raw.notification?.title ?? '',
        body: raw.notification?.body ?? '',
        data: (raw.data ?? {}) as Record<string, string>,
      });
    });
  })();

  return () => {
    cancelled = true;
    if (stop) stop();
  };
}

/**
 * Subscribe to notification-tap events. Returns an unsubscribe function.
 *
 * Fires when the user taps a notification (background or cold-launch).
 * Used to route the user to the URL the server attached to `data.url`.
 *
 *   - Native: Capacitor `notificationActionPerformed`.
 *   - Web: a postMessage from the service worker's `notificationclick`
 *     handler. iOS PWA cold-launches that miss the postMessage race are
 *     handled separately via the IndexedDB drain in apps/mobile App.tsx.
 */
export function onNotificationTapped(
  cb: (payload: { title: string; body: string; data: Record<string, string> }) => void,
): () => void {
  if (isNative()) return onNativePushTapped(cb);

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return () => {};
  }
  const handler = (event: MessageEvent) => {
    const data = event.data as
      | { type?: string; url?: string; title?: string; body?: string }
      | null;
    if (data?.type !== 'notification-click' || typeof data.url !== 'string') return;
    cb({
      title: data.title ?? '',
      body: data.body ?? '',
      data: { url: data.url },
    });
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
